import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Calendar } from 'lucide-react';

import { FounderChip } from '@/features/subscriber/FounderChip';
import { useAuth } from '@/lib/auth/useAuth';
import { contentHref } from '@/lib/contentHref';
import { identityLine } from '@/lib/identityLine';
import { browseQueries } from '@/lib/queries/browse';
import { buildNewForYou, homeQueries, type NewForYouItem } from '@/lib/queries/home';
import { subscriberQueries } from '@/lib/queries/subscriber';
import { subscriptionQueries } from '@/lib/queries/subscription';

export const Route = createFileRoute('/subscriber/')({
  component: SubscriberHome,
});

function monthYear(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function scheduledLabel(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    hour: 'numeric',
    minute: new Date(iso).getMinutes() ? '2-digit' : undefined,
  });
}

function relativeDay(iso: string): string {
  const then = new Date(iso);
  const now = new Date();
  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isToday(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function initial(name: string | null | undefined): string {
  return name?.trim().charAt(0).toUpperCase() || '·';
}

function SubscriberHome() {
  const { user, profile } = useAuth();
  const userId = user?.id;
  const firstName = profile?.display_name?.split(' ')[0] ?? 'there';
  const firstDay = isToday(profile?.created_at);

  const subscription = useQuery(subscriptionQueries.current(userId));
  const stats = useQuery(subscriberQueries.stats());
  const watchCount = useQuery(subscriberQueries.watchCount(userId));
  const continueQuery = useQuery(homeQueries.continueItems(userId));
  const { data: live = [] } = useQuery(browseQueries.liveNow());
  const { data: next = null } = useQuery(browseQueries.nextScheduled());
  const { data: feed = [] } = useQuery(subscriberQueries.feed(userId));
  const { data: candidates = [] } = useQuery(homeQueries.newForYouCandidates());
  const { data: prefs = null } = useQuery(homeQueries.preferences(userId));
  const { data: bookmarks = [] } = useQuery(subscriberQueries.bookmarks(userId));

  const sub = subscription.data;
  const s = stats.data;

  const newForYou = buildNewForYou({
    followed: feed,
    candidates,
    preferenceTags: prefs?.preferred_traditions ?? [],
  });
  const shelf = bookmarks.filter((b) => b.content).slice(0, 3);
  const continueList = continueQuery.data ?? [];
  const liveStream = live[0] ?? null;
  const segments = s
    ? identityLine({
        essays: s.articles_read_count ?? 0,
        // Spec 33: distinct videos watched from watch_progress (a live own-row
        // count), not subscriber_stats.videos_watched_count — that reads
        // time_spent_logs, which the watch page never writes for videos.
        broadcasts: watchCount.data ?? 0,
        readingSeconds: Number(s.total_reading_seconds ?? 0),
      })
    : [];

  return (
    <div className="mx-auto max-w-3xl">
      {/* Header */}
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Your home</p>
          <h1 className="mt-1.5 font-serif text-3xl text-ink sm:text-4xl">
            {firstDay ? 'Welcome' : 'Welcome back'}, {firstName}.
          </h1>
        </div>
        {sub?.is_founder && sub.founder_number !== null && (
          <FounderChip number={sub.founder_number} />
        )}
      </div>

      {/* Row 1 — Continue: the unified resume surface over reading_progress +
          watch_progress (Spec 35), most-recent-first. Videos resume at their
          saved position via the watch route (Spec 33). */}
      {continueList.length > 0 && (
        <Row label="Continue">
          <div className="space-y-2">
            {continueList.map((item) => (
              <Link
                key={item.contentId}
                to={item.href}
                className="group flex items-center gap-4 rounded-lg border border-ink/10 p-3"
              >
                <div className="relative aspect-[16/10] w-24 flex-none overflow-hidden rounded-md bg-ink/5">
                  {item.coverImageUrl && (
                    <img
                      src={item.coverImageUrl}
                      alt={item.coverImageAlt ?? ''}
                      className="h-full w-full object-cover"
                    />
                  )}
                  {item.kind === 'video' && (
                    <span className="absolute bottom-1 left-1 flex h-5 w-5 items-center justify-center rounded-full bg-ink/70 font-sans text-[0.6rem] text-parchment">
                      ▶
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-base text-ink group-hover:text-oxblood">
                    {item.title}
                  </p>
                  <div className="mt-2 h-[3px] max-w-[280px] rounded bg-ink/10">
                    <div
                      className="h-full rounded bg-oxblood"
                      style={{ width: `${item.percent}%` }}
                    />
                  </div>
                  <p className="mt-1.5 font-sans text-xs text-ink/60">
                    {item.percent}% · saved from {relativeDay(item.lastActivityAt)}
                  </p>
                </div>
                <span className="ml-auto flex-none font-sans text-sm text-oxblood">Continue →</span>
              </Link>
            ))}
          </div>
        </Row>
      )}

      {/* Row 2 — Happening now: live (dark card, the one surface-crossing) →
          scheduled (parchment, dashed) → absent. */}
      {liveStream ? (
        <Row label="Happening now">
          <a
            href={liveStream.watchHref}
            className="flex items-center gap-4 rounded-lg bg-video-bg p-4 text-video-text"
          >
            {liveStream.creatorAvatarUrl ? (
              <img
                src={liveStream.creatorAvatarUrl}
                alt=""
                className="h-10 w-10 flex-none rounded-full object-cover ring-2 ring-oxblood-live"
              />
            ) : (
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-video-card font-sans text-sm text-silver ring-2 ring-oxblood-live">
                {initial(liveStream.creatorName)}
              </span>
            )}
            <div className="min-w-0">
              <span className="inline-block rounded-sm bg-oxblood-live px-2 py-0.5 font-sans text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-video-text">
                Live
              </span>
              <p className="mt-1 truncate font-serif text-base">{liveStream.title}</p>
              <p className="font-sans text-xs text-video-text-muted">
                with {liveStream.creatorName} · live now
              </p>
            </div>
            <span className="ml-auto flex-none rounded-md bg-video-text px-4 py-2 font-sans text-sm font-medium text-video-bg">
              Watch
            </span>
          </a>
        </Row>
      ) : next ? (
        <Row label="Next broadcast">
          <div className="flex items-center gap-3 rounded-lg border border-dashed border-ink/20 p-4">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full border border-dashed border-ink/25 text-ink/40">
              <Calendar className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block truncate font-serif text-base text-ink">{next.title}</span>
              <span className="font-sans text-xs text-ink/60">
                {scheduledLabel(next.scheduledFor)}
              </span>
            </span>
          </div>
        </Row>
      ) : null}

      {/* Row 3 — New for you (cascade: followed → tradition → editors). */}
      {newForYou.length > 0 && (
        <Row label="New for you">
          <div>
            {newForYou.map((item) => (
              <NewForYouRow key={item.contentId} item={item} />
            ))}
          </div>
        </Row>
      )}

      {/* Row 4 — Your shelf. */}
      {shelf.length > 0 && (
        <Row label="Your shelf">
          <div className="flex items-end gap-3">
            {shelf.map((b) => {
              const c = b.content!;
              const href = contentHref(c.type, c.slug);
              return (
                <Link key={b.content_id} to={href} className="group w-24">
                  <div className="aspect-[3/4] overflow-hidden rounded bg-ink/5 shadow-sm">
                    {c.cover_image_url && (
                      <img
                        src={c.cover_image_url}
                        alt={c.cover_image_alt ?? ''}
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <p className="mt-1.5 line-clamp-2 font-serif text-xs text-ink/70 group-hover:text-oxblood">
                    {c.title}
                  </p>
                </Link>
              );
            })}
            <Link
              to="/subscriber/saved"
              className="self-center font-sans text-sm text-oxblood hover:underline"
            >
              All saved →
            </Link>
          </div>
        </Row>
      )}

      {/* Foot — the identity line (biography, not surveillance). */}
      <p className="mt-8 flex flex-wrap justify-between gap-2 border-t border-ink/10 pt-4 font-sans text-xs text-ink/50">
        {firstDay || !s ? (
          <span>
            Member since today — your record starts with the first essay.{' '}
            <Link to="/subscriber/discover" className="text-oxblood hover:underline">
              Start reading →
            </Link>
          </span>
        ) : (
          <>
            <span>
              Member since {profile?.created_at ? monthYear(profile.created_at) : '—'}
              {segments.length > 0 && ` · ${segments.join(' · ')}`} —{' '}
              <Link to="/subscriber/history" className="text-oxblood hover:underline">
                Your history →
              </Link>
            </span>
            <Link to="/subscriber/discover" className="text-oxblood hover:underline">
              Discover more →
            </Link>
          </>
        )}
      </p>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <p className="mb-2 font-sans text-[0.7rem] uppercase tracking-[0.18em] text-ink/50">
        {label}
      </p>
      {children}
    </section>
  );
}

function NewForYouRow({ item }: { item: NewForYouItem }) {
  return (
    <Link
      to={item.href}
      className="group flex items-center gap-3 border-b border-ink/[0.07] py-3 last:border-0"
    >
      {item.isVideo ? (
        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-ink/10 font-sans text-xs text-ink/50">
          ▶
        </span>
      ) : item.creatorAvatarUrl ? (
        <img
          src={item.creatorAvatarUrl}
          alt=""
          className="h-7 w-7 flex-none rounded-full object-cover"
        />
      ) : (
        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-ink/10 font-sans text-xs text-ink/50">
          {initial(item.creatorName)}
        </span>
      )}
      <div className="min-w-0">
        {item.attribution && (
          <p className="font-sans text-[0.7rem] text-ink/60">{item.attribution}</p>
        )}
        <p className="font-serif text-sm text-ink group-hover:text-oxblood">{item.title}</p>
      </div>
    </Link>
  );
}
